import React from 'react';
import styled from 'styled-components';
import { Button, Modal } from '@repo/design-system';
import { space } from '@repo/design-tokens';

/*
 * The draft swaps' blocking dialogs. The opening *keep the draft / use production* question
 * that gave this file its name was removed (Rob, 2026-10-27, `remove-draft-choice-modal`); the
 * file keeps its name, since a rename is a separate, mechanical change.
 */

/** Layout only: the type, ink and paragraph rhythm are the `Modal` body's own (card 08). */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  p {
    margin: 0;
  }
`;

/**
 * Shown while *Restore dropped draft* runs, from the flush of unsaved edits until the restored
 * draft is in the wizard. It holds the page still: an edit made in that window would be replaced
 * by the restore, and a save would race it. No action — it closes itself.
 */
export const DraftRestoringModal: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <Modal
      open
      onClose={() => undefined}
      dismissible={false}
      title="Restoring the dropped draft"
      width="md"
    >
      <Body>
        <p>Saving your latest edits, then bringing back the dropped draft. This takes a moment.</p>
      </Body>
    </Modal>
  ) : null;

/**
 * A swap that may have landed but could not be confirmed (R3.4, R4.4 — unknown outcome). The wizard
 * cannot know which version the server holds, and an autosave now would write the version on
 * screen over whatever is there; so nothing else is offered until a reload.
 */
export const DraftReloadModal: React.FC<{ message: string | null; onReload: () => void }> = ({
  message,
  onReload,
}) =>
  message ? (
    <Modal
      open
      onClose={() => undefined}
      dismissible={false}
      title="Reload to continue"
      tone="butter"
      width="md"
      // `autoFocus`: Reload is the only way out of a dialog that cannot be dismissed, so focus lands
      // here and Enter or Space reloads (`plan-save-version-check`, criterion 13).
      actions={
        <Button type="button" variant="primary" autoFocus onClick={onReload}>
          Reload
        </Button>
      }
    >
      <Body>
        <p>{message}</p>
        <p>
          Nothing more is saved until you reload, so your work cannot overwrite the other version.
        </p>
      </Body>
    </Modal>
  ) : null;
